import styles from "../AddMarkerModal/AddIncidentModal.module.css";
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import { TextField, FormControl, MenuItem, Select } from "@mui/material";
import useAppSnackbar from "../../../../hooks/useAppSnackbar";
import { useFetchGetGeofenceById } from "../../../../hooks/guide/useFetchGetGeofenceById";
import { useFetchAllGroupGeofence } from "../../../../hooks/guide/useFetchAllGroupGeofence";
import { geofenceService } from "../../../../services/guide/geofence-service";
import { UpdateGeofence } from "../../../../types/api/guide/geofence.types";

export type EditGeofenceModalHandle = {
  save: () => Promise<void>;
};

interface EditGeofenceModalProps {
  id: string;
  onClose: () => void;
  refetchData: () => void;
  getPointsFromMap?: () => Array<[number, number]>;
  onStartEditContour?: () => void;
  isContourEdited?: boolean;
  isCollapsed?: boolean;
}

const EditGeofenceModal = forwardRef<
  EditGeofenceModalHandle,
  EditGeofenceModalProps
>(function EditGeofenceModal(
  {
    id,
    onClose,
    refetchData,
    getPointsFromMap,
    onStartEditContour,
    isContourEdited,
    isCollapsed,
  },
  ref,
) {
  const { showSuccess, showError, SnackbarComponent } =
    useAppSnackbar();

  const { data: geofence, isLoading, error } = useFetchGetGeofenceById(id);
  const { data: groups = [] } = useFetchAllGroupGeofence();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [coordinates, setCoordinates] = useState("");
  const [groupGeofenceId, setGroupGeofenceId] = useState<string>("");

  useEffect(() => {
    if (!geofence) return;

    setName(geofence.name ?? "");
    setDescription(geofence.description ?? "");
    setCoordinates(geofence.coordinates ?? "");
    setGroupGeofenceId((geofence as any).groupGeofenceId ?? "");
  }, [geofence]);

  const canSave = useMemo(() => {
    if (!id) return false;
    if (!name.trim()) return false;

    const ring = getPointsFromMap?.() ?? [];
    if (!Array.isArray(ring) || ring.length < 3) return false;

    return true;
  }, [id, name, getPointsFromMap]);

  const handleSave = async () => {
    try {
      const ring = getPointsFromMap?.();
      if (!ring || ring.length < 3) {
        showError("Контур геозоны не выбран или содержит меньше 3 точек");
        return;
      }

      const payload: UpdateGeofence = {
        id,
        name: name.trim(),
        description: description.trim(),
        points: ring.map(([lat, lon]) => ({ latitude: lat, longitude: lon })),
        groupGeofenceId: groupGeofenceId || undefined,
      } as any;

      await geofenceService.update(id, payload);

      showSuccess("Геозона обновлена");
      refetchData?.();
      onClose();
    } catch (e: any) {
      showError(e?.response?.data ?? "Ошибка сохранения геозоны");
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  return (
    <div
      className={styles.modalOverlay}
      style={{
        opacity: isCollapsed ? 0 : 1,
        pointerEvents: isCollapsed ? "none" : "auto",
        transition: "opacity 0.2s ease",
      }}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Редактировать геозону</h3>
          <button className={styles.modalClose} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {isLoading && <div style={{ opacity: 0.7 }}>Загрузка...</div>}
          {error && <div style={{ color: "red" }}>Ошибка загрузки геозоны</div>}

          {!isLoading && !error && (
            <>
              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Название</div>
                <div className={styles.modalCoords}>
                  <TextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                  />
                </div>
              </div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Описание</div>
                <div className={styles.modalCoords}>
                  <TextField
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </div>
              </div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Группа геозон</div>
                <div className={styles.modalCoords}>
                  <FormControl fullWidth variant="outlined">
                    <Select
                      value={groupGeofenceId}
                      displayEmpty
                      onChange={(e) =>
                        setGroupGeofenceId(String(e.target.value))
                      }
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span style={{ color: "#9ca3af" }}>
                              Выберите группу
                            </span>
                          );
                        }
                        const selectedGroup = groups.find(
                          (g: any) => g.id === selected,
                        );
                        return selectedGroup?.name || "";
                      }}
                      MenuProps={{
                        disablePortal: true,
                        PaperProps: { sx: { zIndex: 20000 } },
                      }}
                    >
                      <MenuItem value="">
                        <em>Не выбрано</em>
                      </MenuItem>

                      {groups.map((g: any) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>

              <div className={styles.modalField}>
                <div className={styles.modalLabel}>Координаты</div>
                <div className={styles.modalCoords}>
                  <TextField
                    value={coordinates}
                    fullWidth
                    disabled
                    multiline
                    minRows={3}
                  />
                </div>
              </div>

              <div style={{ opacity: 0.7, marginTop: 8 }}>
                Чтобы редактировать геозону, нажмите ПКМ по геозоне на карте.
                Точки геозоны сохраняется из карты: нажми “Редактировать
                геозону”, передвиньте точки, затем нажмите Сохранить(завершить).
              </div>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.modalButton} onClick={onClose}>
            Отмена
          </button>

          <button
            className={styles.modalButton}
            onClick={() => {
              onStartEditContour?.();
            }}
          >
            Редактировать геозону
          </button>

          <button
            className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
            onClick={handleSave}
            disabled={!canSave || isLoading || !!error}
            style={{ opacity: !canSave || isLoading || !!error ? 0.6 : 1 }}
          >
            Сохранить
          </button>
        </div>
      </div>

      <SnackbarComponent />
    </div>
  );
});

export default EditGeofenceModal;
